"use client";

import React from "react";
import { CircleAlert as AlertCircle, ArrowRight, CircleCheck as CheckCircle2, Clock, CircleQuestionMark as HelpCircle, Mail, Package, RotateCcw, ShieldAlert, Truck } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
export default function Refund() {
  const contactEmail = "sales@mechanicalbullshop.com";
  return <section id="refundandreturnspolicy" data-nav="light" className="w-full bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <header className="space-y-4 pb-8 border-b border-border">
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant="outline" className="font-mono text-xs uppercase tracking-wider py-1 px-2.5">
              Legal & Purchasing Terms
            </Badge>
            <span className="text-xs font-mono text-muted-foreground">
              Last updated: September 18, 2026
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold tracking-tight uppercase text-foreground">
            Refund & Returns Policy
          </h1>

          <div className="space-y-3 text-base md:text-lg text-muted-foreground leading-relaxed pt-2">
            <p>
              Mechanical Bull Shop sells mechanical bulls, parts and accessories. Because complete mechanical bulls are large equipment purchases, returns require more planning than a typical parcel return. This policy explains cancellations, deposits, returns, return shipping and refunds.
            </p>
            <p className="font-medium text-foreground">
              Please review these terms before making a payment or submitting an order.
            </p>
          </div>
        </header>

        {/* Policy Content Sections */}
        <div className="mt-10 space-y-12">
          {/* Section 1: Order Preparation and Payment */}
          <article className="space-y-4">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary shrink-0" />
              <h2 className="text-2xl md:text-3xl font-serif font-bold uppercase tracking-tight text-foreground">
                Order Preparation and Payment
              </h2>
            </div>
            <div className="space-y-3 text-foreground/90 leading-relaxed text-sm md:text-base">
              <p>
                Mechanical bulls are usually prepared and ready for scheduled shipping within approximately 2 weeks after we receive either the required deposit or full payment.
              </p>
              <p>
                If a deposit is used, the remaining balance must be paid in full before the scheduled shipping date. A machine will not be released for shipment until the required order balance has been received.
              </p>
              <div className="p-4 bg-muted/60 border border-border text-xs md:text-sm text-muted-foreground font-mono">
                Notice: Approximately 2 weeks is a normal preparation period for our fabrication and testing schedule, not a guaranteed delivery date.
              </div>
            </div>
          </article>

          <Separator className="bg-border" />

          {/* Section 2: Deposits and Cancellations Before Shipping */}
          <article className="space-y-4">
            <div className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-primary shrink-0" />
              <h2 className="text-2xl md:text-3xl font-serif font-bold uppercase tracking-tight text-foreground">
                Deposits and Cancellations Before Shipping
              </h2>
            </div>
            <div className="space-y-3 text-foreground/90 leading-relaxed text-sm md:text-base">
              <p>
                The standard deposit is 20% of the product price.
              </p>
              <p>
                If you change your mind, the deposit is refundable when the cancellation request is received on or before the scheduled shipping date.
              </p>
              <p>
                Once the order has shipped, the cancellation period ends and the standard return policy below applies.
              </p>
              <p>
                To request a cancellation, contact{" "}
                <a href={`mailto:${contactEmail}`} className="font-medium text-primary hover:underline">
                  {contactEmail}
                </a>{" "}
                with your name and the product or order information.
              </p>
            </div>
          </article>

          <Separator className="bg-border" />

          {/* Section 3: 20-Day Return Window */}
          <article className="space-y-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
              <h2 className="text-2xl md:text-3xl font-serif font-bold uppercase tracking-tight text-foreground">
                20-Day Return Window
              </h2>
            </div>
            <div className="space-y-3 text-foreground/90 leading-relaxed text-sm md:text-base">
              <p>
                You may request a return within 20 calendar days of the date your order is delivered.
              </p>
              <p>
                Contact Mechanical Bull Shop before sending any equipment or parts back. Returns sent without prior return instructions may be delayed or difficult to identify.
              </p>
            </div>
          </article>

          <Separator className="bg-border" />

          {/* Section 4: Return Condition */}
          <article className="space-y-4">
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5 text-primary shrink-0" />
              <h2 className="text-2xl md:text-3xl font-serif font-bold uppercase tracking-tight text-foreground">
                Return Condition
              </h2>
            </div>
            <div className="space-y-3 text-foreground/90 leading-relaxed text-sm md:text-base">
              <p>
                Returned equipment should be in substantially the same condition in which it was delivered and should include the components and accessories supplied with the order.
              </p>
              <p>
                Normal inspection of the equipment does not by itself prevent a return. However, damage caused after delivery through misuse, improper handling, unauthorized modifications or avoidable damage may affect return eligibility or the refundable amount.
              </p>
            </div>
          </article>

          <Separator className="bg-border" />

          {/* Section 5: If You Change Your Mind */}
          <article className="space-y-4">
            <div className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-primary shrink-0" />
              <h2 className="text-2xl md:text-3xl font-serif font-bold uppercase tracking-tight text-foreground">
                If You Change Your Mind
              </h2>
            </div>
            <p className="text-foreground/90 leading-relaxed text-sm md:text-base">
              If the product is not defective and the return is simply because you changed your mind, you are responsible for arranging and paying the return shipping costs. The original shipping or freight charge is not refundable when the return is due to a change of mind. Mechanical Bull Shop does not charge a restocking fee.
            </p>

            {/* Three Visually Distinct Key Policy Points */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
              <div className="p-4 bg-muted/40 border border-border flex flex-col justify-between">
                <span className="text-xs uppercase font-mono text-muted-foreground block mb-1">
                  Freight & Shipping
                </span>
                <span className="font-bold text-foreground text-sm md:text-base">
                  Return shipping: Customer responsibility
                </span>
              </div>
              <div className="p-4 bg-muted/40 border border-border flex flex-col justify-between">
                <span className="text-xs uppercase font-mono text-muted-foreground block mb-1">
                  Outbound Freight
                </span>
                <span className="font-bold text-foreground text-sm md:text-base">
                  Original shipping charge: Non-refundable for change-of-mind returns
                </span>
              </div>
              <div className="p-4 bg-muted/40 border border-border flex flex-col justify-between">
                <span className="text-xs uppercase font-mono text-muted-foreground block mb-1">
                  Processing Fee
                </span>
                <span className="font-bold text-foreground text-sm md:text-base">
                  Restocking fee: null
                </span>
              </div>
            </div>
          </article>

          <Separator className="bg-border" />

          {/* Section 6: Incorrect or Problem Orders */}
          <article className="space-y-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-primary shrink-0" />
              <h2 className="text-2xl md:text-3xl font-serif font-bold uppercase tracking-tight text-foreground">
                If We Supplied the Wrong Item or Caused the Issue
              </h2>
            </div>
            <div className="space-y-3 text-foreground/90 leading-relaxed text-sm md:text-base">
              <p>
                If you believe Mechanical Bull Shop supplied the wrong product or the issue resulted from our error, contact us as soon as reasonably possible.
              </p>
              <p>
                Include your order information, a description of the issue and photographs where useful. Once the issue is reviewed and confirmed, you will not be responsible for reasonable return shipping required to correct an error caused by us.
              </p>
            </div>
          </article>

          <Separator className="bg-border" />

          {/* Section 7: Items Damaged During Delivery */}
          <article className="space-y-4">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-primary shrink-0" />
              <h2 className="text-2xl md:text-3xl font-serif font-bold uppercase tracking-tight text-foreground">
                Items Damaged During Delivery
              </h2>
            </div>
            <div className="space-y-3 text-foreground/90 leading-relaxed text-sm md:text-base">
              <p>
                If your order arrives with visible shipping damage, photograph the packaging and affected equipment as soon as possible and contact{" "}
                <a href={`mailto:${contactEmail}`} className="text-primary hover:underline font-medium">
                  {contactEmail}
                </a>
                .
              </p>
              <p>
                Keep the packaging and relevant shipment information until the issue has been reviewed.
              </p>
              <p>
                Shipping-damage cases are reviewed individually because the appropriate resolution may depend on the equipment, damage and delivery arrangement.
              </p>
            </div>
          </article>

          <Separator className="bg-border" />

          {/* Section 8: Returning a Complete Mechanical Bull */}
          <article className="space-y-4">
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5 text-primary shrink-0" />
              <h2 className="text-2xl md:text-3xl font-serif font-bold uppercase tracking-tight text-foreground">
                Returning a Complete Mechanical Bull
              </h2>
            </div>
            <p className="text-foreground/90 leading-relaxed text-sm md:text-base">
              Complete mechanical bulls are large pieces of equipment, so every return must be arranged with Mechanical Bull Shop before the equipment is sent back.
            </p>
            <div className="p-5 bg-muted/30 border border-border space-y-3">
              <p className="font-semibold text-foreground text-sm uppercase tracking-wide font-mono">
                Before returning a complete bull:
              </p>
              <ul className="space-y-2 text-sm md:text-base text-foreground/90 list-disc list-inside">
                <li>contact us within the 20-day return window</li>
                <li>identify the exact machine</li>
                <li>explain the reason for the return</li>
                <li>include relevant photographs where appropriate</li>
                <li>wait for return instructions</li>
              </ul>
            </div>
            <p className="font-semibold text-foreground text-sm md:text-base">
              Do not ship a mechanical bull back without receiving return instructions first.
            </p>
          </article>

          <Separator className="bg-border" />

          {/* Section 9: Parts & Accessories Returns */}
          <article className="space-y-4">
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5 text-primary shrink-0" />
              <h2 className="text-2xl md:text-3xl font-serif font-bold uppercase tracking-tight text-foreground">
                Parts &amp; Accessories Returns
              </h2>
            </div>
            <div className="space-y-3 text-foreground/90 leading-relaxed text-sm md:text-base">
              <p>
                Parts and accessories may also be returned within 20 calendar days of delivery, subject to the same general return requirements.
              </p>
              <p>
                The product should be returned in substantially the same condition in which it was delivered and with its included components where applicable.
              </p>
              <p>
                If a part was ordered incorrectly or the buyer simply changed their mind, the buyer is responsible for return shipping and the original shipping charge is not refundable.
              </p>
              <p>
                No restocking fee applies.
              </p>
              <p className="text-xs md:text-sm text-muted-foreground font-mono pt-1">
                Note: Installed electrical components are subject to individual review if their condition makes them unsuitable for resale.
              </p>
            </div>
          </article>

          <Separator className="bg-border" />

          {/* Section 10: How to Request a Return */}
          <article className="space-y-4">
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary shrink-0" />
              <h2 className="text-2xl md:text-3xl font-serif font-bold uppercase tracking-tight text-foreground">
                How to Request a Return
              </h2>
            </div>
            <p className="text-foreground/90 leading-relaxed text-sm md:text-base">
              Contact:{" "}
              <a href={`mailto:${contactEmail}`} className="font-medium text-primary hover:underline">
                {contactEmail}
              </a>
            </p>
            <div className="p-5 bg-muted/30 border border-border space-y-3">
              <p className="font-semibold text-foreground text-sm uppercase tracking-wide font-mono">
                Include:
              </p>
              <ul className="space-y-2 text-sm md:text-base text-foreground/90 list-disc list-inside">
                <li>full name</li>
                <li>product name</li>
                <li>order or enquiry reference if available</li>
                <li>reason for return</li>
                <li>photographs if relevant</li>
              </ul>
            </div>
            <p className="font-semibold text-foreground text-sm md:text-base">
              Wait for return instructions before shipping anything back.
            </p>
          </article>

          <Separator className="bg-border" />

          {/* Section 11: Refunds */}
          <article className="space-y-4">
            <div className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-primary shrink-0" />
              <h2 className="text-2xl md:text-3xl font-serif font-bold uppercase tracking-tight text-foreground">
                Refunds
              </h2>
            </div>
            <div className="space-y-3 text-foreground/90 leading-relaxed text-sm md:text-base">
              <p>
                After an approved return is received and reviewed, Mechanical Bull Shop will confirm the refundable amount.
              </p>
              <p>
                For an ordinary change-of-mind return, the refund covers the approved product amount but does not include the original shipping or freight charge.
              </p>
              <p>
                There is no restocking fee.
              </p>
              <p>
                If Mechanical Bull Shop caused the issue, applicable shipping costs required to correct the problem will be handled as part of the resolution.
              </p>
              <p className="text-xs md:text-sm text-muted-foreground font-mono">
                After a refund is issued, a bank or payment provider may require additional processing time before the funds appear in the customer's account.
              </p>
            </div>
          </article>

          <Separator className="bg-border" />

          {/* Section 12: Warranty */}
          <article className="space-y-4">
            <div className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-primary shrink-0" />
              <h2 className="text-2xl md:text-3xl font-serif font-bold uppercase tracking-tight text-foreground">
                Returns and Warranty Claims Are Different
              </h2>
            </div>
            <div className="space-y-3 text-foreground/90 leading-relaxed text-sm md:text-base">
              <p>
                A return and a warranty claim are not necessarily the same thing. Any warranty applying to a mechanical bull, part or accessory depends on the exact product and the warranty information provided for that purchase.
              </p>
              <p>
                Do not assume warranty coverage exists unless it is stated for the individual product or order.
              </p>
            </div>
          </article>

          <Separator className="bg-border" />

          {/* Section 13: Questions */}
          <article className="p-6 md:p-8 bg-muted/40 border border-border space-y-4">
            <h2 className="text-2xl md:text-3xl font-serif font-bold uppercase tracking-tight text-foreground">
              Questions About Returns or Refunds
            </h2>
            <p className="text-foreground/90 leading-relaxed text-sm md:text-base">
              If you are unsure whether your order qualifies for cancellation, return or refund, contact us before sending anything back:
            </p>
            <div>
              <Button asChild variant="default" className="gap-2">
                <a href={`mailto:${contactEmail}`}>
                  <Mail className="w-4 h-4" />
                  <span>{contactEmail}</span>
                  <ArrowRight className="w-4 h-4" />
                </a>
              </Button>
            </div>
          </article>
        </div>
      </div>
    </section>;
}